import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { bugsAPI, projectsAPI, tasksAPI, usersAPI, activitiesAPI, exportAPI, downloadBlob } from '../utils/api';
import { PageHeader, Badge, SearchInput, Modal, FormField, EmptyState, ConfirmDialog } from '../components/layout/UI';
import { useAuth } from '../hooks/useAuth';
import BugFormFields, { bugToForm, bugFormValid, emptyBugForm } from '../components/BugFormFields';
import { isAdminRole } from '../utils/rolesConfig';
import { apiError, useConfirm, useToast } from '../components/Toast';
import { usePagePerms } from '../PagePermsContext';
import { useMasters } from '../hooks/useMasters';
import { Bug, Download, Paperclip, Plus, RotateCcw, SquarePen, Trash2 } from 'lucide-react';
import Pager, { usePaging } from '../components/Pager';
import { useSelection, SelectAll, RowCheck, BulkBar } from '../components/BulkActions';

const P = () => <Plus size={14} />;
const Pen = () => <SquarePen size={13} />;
const Tr = () => <Trash2 size={13} />;
const Dl = () => <Download size={14} />;
const Clip = () => <Paperclip size={11} />;

// Must match bugs_status_check — all ten values, or bugs in the missing
// statuses render an empty <select>.
/* Fallbacks only; the live lists come from Masters inside the component,
 * the same categories BugFormFields reads, so a filter cannot offer a
 * status the form does not. */
const S_FALLBACK = ['Open','In Progress','Fixed in Dev','In Testing','Fixed',
                'Deployed to UAT','Deployed to Production','Resolved',
                'Verified','Closed'].map(v=>({value:v,label:v}));
const SEV_FALLBACK = ['Minor','Major','Critical'].map(v=>({value:v,label:v}));
const ENV_STYLE = { Production:'border border-red-500 text-red-600 dark:text-red-400', UAT:'border border-amber-500 text-amber-600 dark:text-amber-400', Development:'border border-blue-500 text-blue-600 dark:text-blue-400' };
const defaultForm = { project_id:'', task_id:'', activity_id:'', title:'', description:'', steps_to_reproduce:'', severity:'Minor', status:'Open', assigned_to:'', environment:'Development' };
// Unassigned work belongs to nobody and appears on nobody's list, so the
// picker defaults to the current user and cannot be left empty.

export default function BugsPage() {
  // Admin → Page Access decides whether this role may create here.
  const { canWrite } = usePagePerms();
  const m = useMasters({ status: ['bug_status', S_FALLBACK],
                         severity: ['bug_severity', SEV_FALLBACK] });
  const toast = useToast();
  const confirm = useConfirm();
  const { user: me } = useAuth();
  const [searchParams] = useSearchParams();
  const projectFilter = searchParams.get('project_id')||'';
  const navigate = useNavigate();
  const [bugs, setBugs] = useState([]);
  const [error, setError] = useState(''); const [projects, setProjects] = useState([]); const [tasks, setTasks] = useState([]); const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true); const [search, setSearch] = useState('');
  const [scope, setScope] = useState('all');   // all | mine | deleted
  // One pager for every list, so they all count and phrase it the same way.
  const paging = usePaging(scope);
  const [statusFilter, setStatusFilter] = useState(''); const [severityFilter, setSeverityFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false); const [editBug, setEditBug] = useState(null);
  const [form, setForm] = useState({...defaultForm, project_id:projectFilter});
  const [files, setFiles] = useState([]); const [saving, setSaving] = useState(false); const [deleteId, setDeleteId] = useState(null);
  const [taskActivities, setTaskActivities] = useState([]);

  // Activities of the selected task — an error is often found on one specific
  // activity, which the task-level link alone cannot express.
  useEffect(() => {
    if (!form.task_id) { setTaskActivities([]); return; }
    let cancelled = false;
    activitiesAPI.getByTask(form.task_id)
      .then(r => { if (!cancelled) setTaskActivities(r.data?.activities || r.data || []); })
      .catch(() => { if (!cancelled) setTaskActivities([]); });
    return () => { cancelled = true; };
  }, [form.task_id, scope, paging.offset]);



  const restoreRow = async (row) => {
    try { await bugsAPI.restore(row.id); toast.success('Restored'); load(); }
    catch (e) { toast.error(apiError(e, 'Could not restore it.')); }
  };

  const discardRow = async (row) => {
    const ok = await confirm(
      'Remove this from your Deleted tab? You will not be able to see it after '
      + 'this, though an administrator can still restore it.',
      { title: 'Remove from Deleted', confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try { await bugsAPI.discard(row.id); toast.success('Removed from your Deleted tab'); load(); }
    catch (e) { toast.error(apiError(e, 'Could not remove it.')); }
  };
  // Ticked rows. Scoped to what is on screen, so a filter cannot hide
  // something that is still selected.
  const sel = useSelection(bugs);

  const load = useCallback(async () => {
    try {
      const [bR,pR,uR,tR] = await Promise.all([
        bugsAPI.getAll({ project_id:projectFilter, status:statusFilter, severity:severityFilter, search, scope: scope === 'deleted' ? 'deleted' : undefined, mine: scope === 'mine' ? 'true' : undefined, limit: paging.limit, offset: paging.offset }),
        projectsAPI.getAll({ limit: 0 }), usersAPI.getAll(),
        tasksAPI.getAll({ project_id:projectFilter || undefined , limit: 0 }),
      ]);
      setBugs(bR.data); setProjects(pR.data); setUsers(uR.data); setTasks(tR.data);
      paging.readHeaders(bR);
    } catch(e) { toast.error(apiError(e, 'Something went wrong loading this.')); } finally{setLoading(false);}
  }, [projectFilter, statusFilter, severityFilter, search]);
  useEffect(()=>{load();},[load]);

  const openCreate = () => { setEditBug(null); setForm({ ...emptyBugForm(), project_id:projectFilter, assigned_to:me?.id||'' }); setFiles([]); setModalOpen(true); };
  const openEdit = (b) => { setEditBug(b); setForm(bugToForm(b)); setModalOpen(true); };
  const handleSave = async () => {
    setSaving(true);
    try {
      if (editBug) { await bugsAPI.update(editBug.id, form); }
      else          { await bugsAPI.create(form); }
      toast.success(editBug ? 'Bug updated' : 'Bug reported');
      setModalOpen(false); load();
    } catch(e){ toast.error(apiError(e, 'Failed to save bug')); } finally{setSaving(false);}
  };
  const handleDelete = async () => {
    try { await bugsAPI.delete(deleteId); toast.success('Bug deleted'); }
    catch(e){ toast.error(apiError(e, 'Failed to delete bug')); }
    finally { setDeleteId(null); load(); }
  };


  const critCount = bugs.filter(b=>b.severity==='Critical'&&!['Closed','Verified'].includes(b.status)).length;

  return (
    <div>
      {error && (
        <div className="pt-2">
          <div className="p-3 rounded-xl text-sm mb-2" style={{ background:'rgba(239,68,68,.08)', color:'#ef4444', border:'1px solid rgba(239,68,68,.2)' }}>
            ⚠ {error} <button className="ml-2 underline text-xs" onClick={()=>setError('')}>Dismiss</button>
          </div>
        </div>
      )}
      <PageHeader title="Bug Tracker" subtitle="Track and resolve bugs across projects"
        actions={<><button onClick={async()=>{const r=await exportAPI.exportBugs(projectFilter);downloadBlob(r.data,'bugs.xlsx');}} className="btn-secondary flex items-center gap-1.5 text-sm"><Dl /> Export</button>{canWrite && (<button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm"><P /> Report Bug</button>)}</>} />

      {critCount > 0 && <div className="mb-4 flex items-center gap-2 p-3 rounded-xl border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">⚠️ {critCount} critical bug{critCount>1?'s':''} require immediate attention</div>}

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="flex-1 min-w-48 max-w-xs"><SearchInput value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search bugs..." /></div>
        {/* All / Mine / Deleted. Bugs and change requests were the last two
            entities where deleting destroyed the record. */}
        <div className="flex rounded-lg border overflow-hidden text-sm self-start"
          style={{ borderColor:'var(--border)' }}>
          {[['all','All'],['mine','Mine'],['deleted','Deleted']].map(([v,l]) => (
            <button key={v} onClick={() => setScope(v)}
              className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1.5 ${scope===v ? 'bg-brand-500 text-white' : ''}`}
              style={scope!==v ? { color:'var(--text-secondary)', background:'var(--surface)' } : {}}>
              {v === 'deleted' && <Trash2 size={13} />}
              {l}
            </button>
          ))}
        </div>
        <select className="select w-36 text-sm" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}><option value="">All Status</option>{m.status.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select>
        <select className="select w-36 text-sm" value={severityFilter} onChange={e=>setSeverityFilter(e.target.value)}><option value="">All Severity</option>{m.severity.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select>
      </div>

      {loading ? <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>
      : bugs.length===0 ? <div className="card"><EmptyState icon={Bug} title="No bugs found" description="All clear! Report a bug if you find one." action={canWrite && (<button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm"><P /> Report Bug</button>)} /></div>
      : (
        <div className="card overflow-hidden p-0">
          <table className="w-full text-sm"><thead><tr className="border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
              <th className="px-4 py-3 w-10">
                <SelectAll allChecked={sel.allChecked} someChecked={sel.someChecked}
                  onToggle={sel.toggleAll} />
              </th>
            {['Title','Project','Severity','Status','Environment','Assigned',''].map(h=>(
              <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {bugs.map(b=>(
              <tr key={b.id} className="border-b hover:bg-[var(--surface-alt)] transition-colors cursor-pointer" style={{ borderColor:'var(--border)' }}
                onClick={() => navigate(`/bugs/${b.id}`)}>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <RowCheck checked={sel.isSelected(b.id)}
                      onToggle={() => sel.toggle(b.id)} label={b.title} />
                  </td>
                <td className="px-4 py-3">
                  <p className="font-medium" style={{ color:'var(--text)' }}>{b.title}</p>
                  {b.task_title&&<p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>Task: {b.task_title}</p>}
                  {b.attachments?.length>0&&<span className="flex items-center gap-1 text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}><Clip/>{b.attachments.length}</span>}
                </td>
                <td className="px-4 py-3 text-xs hidden md:table-cell" style={{ color:'var(--text-secondary)' }}>{b.project_name}</td>
                <td className="px-4 py-3"><Badge value={b.severity} type="severity" /></td>
                <td className="px-4 py-3">
                  <select value={b.status} onChange={async e=>{await bugsAPI.update(b.id,{...b,status:e.target.value});
                  toast.success('Bug updated');load();}}
                    className="text-xs rounded-lg px-2 py-1 border font-medium cursor-pointer outline-none" style={{ background:'var(--surface)', borderColor:'var(--border)', color:'var(--text)' }}>
                    {m.status.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">{b.environment&&<span className={`badge ${ENV_STYLE[b.environment]||''}`}>{b.environment}</span>}</td>
                <td className="px-4 py-3 hidden lg:table-cell text-xs" style={{ color:'var(--text-secondary)' }}>{b.assigned_to_name||'—'}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {scope === 'deleted' ? (
                      <>
                        {/* In the bin: put it back, or take it out of your own Deleted tab. */}
                        <button onClick={e=>{e.stopPropagation();restoreRow(b);}}
                          className="text-xs px-2.5 py-1 rounded-lg border flex items-center gap-1 hover:bg-[var(--surface-alt)]"
                          style={{ borderColor:'var(--border)', color:'#2E539F' }}>
                          <RotateCcw size={12} /> Restore
                        </button>
                        <button onClick={e=>{e.stopPropagation();discardRow(b);}}
                          className="text-xs px-2.5 py-1 rounded-lg border flex items-center gap-1 hover:bg-red-50 dark:hover:bg-red-900/20"
                          style={{ borderColor:'var(--border)', color:'#ef4444' }}>
                          <Trash2 size={12} /> Delete
                        </button>
                      </>
                    ) : (
                      <>
                    <button onClick={e=>{e.stopPropagation();openEdit(b);}} className="p-1.5 rounded hover:bg-[var(--surface-alt)] transition-colors" style={{ color:'var(--text-secondary)' }}><Pen /></button>
                    {(b.created_by === me?.id || isAdminRole(me?.role)) && (
                      <button onClick={e=>{e.stopPropagation();setDeleteId(b.id);}} title="Delete"
                        className="p-1.5 rounded text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"><Tr /></button>
                    )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody></table>
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={()=>setModalOpen(false)} title={editBug?'Edit Bug':'Report Bug'} size="lg">
        <div className="space-y-4">
          {/* Shared with DevHubPage — add fields in BugFormFields.jsx, not here. */}
          <BugFormFields form={form} setForm={setForm}
            projects={projects} users={users} me={me} showStatus />
          {!editBug && (
            <FormField label="Attachments">
              <input type="file" multiple className="input" onChange={e=>setFiles([...e.target.files])} />
              {files.length>0 && <p className="text-xs mt-1" style={{ color:'var(--text-secondary)' }}>{files.length} file(s) selected</p>}
            </FormField>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={()=>setModalOpen(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleSave} disabled={saving||!bugFormValid(form)} className="btn-primary flex items-center gap-2">{saving&&<span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}{editBug?'Update':'Report'} Bug</button>
          </div>
        </div>
      </Modal>
      <ConfirmDialog isOpen={!!deleteId} onClose={()=>setDeleteId(null)} onConfirm={handleDelete} title="Delete Bug" message="Delete this bug permanently?" />

    <Pager {...paging} onChange={paging.setOffset} noun="bugs" />
    <BulkBar bulk={bugsAPI.bulk} view={scope === 'deleted' ? 'deleted' : 'live'}
      selected={sel.selected} label="bug" labelPlural="bugs"
      onClear={sel.clear} onDone={load} />
    </div>
  );
}
